import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableAlertsStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportAlertsStation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  fetchAlertsReportStation,
  setReportFilterData,
  setReportFiltersData,
  updateReportFilters,
  fetchListFilters,
  setSortData,
  setAlertsStationReportUpdate,
} from '../../../../redux/slice/ReportAlertsStationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slice/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationViewSidePanel from '../../../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/report';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter?: any;
  clearDateRange?: () => void;
}

/**
 * Reports management page
 */
function AlertsReportTableStation(properties: ReportTableProperties) {
  const { leftTopContent, preferenceFilter, clearDateRange } = properties;
  const { t } = useTranslation('reports');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isAlertsStationReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportAlertsStation);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);

  const column = [
    {
      label: t('stationInfo'),
      accessorKey: 'stationInfo' as keyof AvailableAlertsStationReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableAlertsStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'createdAt',
      queryParamsValue: 'createdAt',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      sortAllow: true,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
    {
      label: t('basePricingAlertsConsumption'),
      accessorKey: 'basePricePowerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },

    {
      label: t('peakPricingAlertsConsumption'),
      accessorKey: 'peakPricePowerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
    {
      label: t('offPeakPricingAlertsConsumption'),
      accessorKey: 'offPeakPricePowerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
    {
      label: t('scheduleChargeAlertsConsumption'),
      accessorKey: 'scheduledPowerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
  ];

  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAlertsReportStation({
        ...queryParameters,
        sortField,
        sortBy,
        page: pageCount,
        limit,
      }),
    );
  };
  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAlertsReportStation({
        ...queryParameters,
        sortBy,
        sortField,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page,
        limit,
      }),
    );
  };
  /**
   *  Handle the filter data clear function
   * @param filterData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setReportFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    dispatch(
      fetchAlertsReportStation({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAlertsReportStation({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit: limitCount,
      }),
    );
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    reportApi
      .fetchReportListExport({ ...queryParameters })
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          t('date'),
          t('stationId'),
          t('stationName'),
          t('powerConsumed'),
          t('revenueGenerated'),
        ];
        const rows = response.data.map((row: AvailableAlertsStationReport, index: number) => [
          index + 1,
          row.createdAt ? ConvertUTCtoDate(row.createdAt) : '',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${t('reportsList')}.csv`);
          } else {
            exportPDF(headers, rows, `${t('reportsList')}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };
  /**
   * Fetches Alerts report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAlertsReportStation({
          ...data,
        }),
      );
    }
  };
  const fetchReportData = useCallback(() => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);
    if (!selectedFacility?._id) return;

    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    currentFacilityTagsList,
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isAlertsStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setAlertsStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isAlertsStationReportUpdate]);

  useEffect(() => {
    if (filters.length === 1) {
      dispatch(fetchListFilters());
    }
    if (preferenceFilter.pageName === 'alertsReport' && reportsData.reportByType === 'station') {
      if (filters.length > 1) {
        const setFilter = filters.map((filter) => {
          let returnValue = filter;
          const single = preferenceFilter.filter?.find(
            (preference: any) => preference.field === filter.key,
          );
          if (single && single?.value.length > 0) {
            returnValue = {
              ...returnValue,
              value: single?.value,
              applyFilter: true,
              valueToShow: returnValue?.listOfValue
                ?.filter((show) => single?.value?.includes(show.value))
                .map((show) => show.label),
            };
          } else {
            returnValue = {
              ...returnValue,
              value: [],
              applyFilter: false,
              valueToShow: [],
            };
          }
          return returnValue;
        });
        dispatch(setReportFiltersData(setFilter));
        const queryParameters = GetParametersFromCommonFilters(setFilter);
        fetchData({
          ...queryParameters,
          startDate: reportsData.reportFilter.startDate,
          endDate: reportsData.reportFilter.endDate,
          page: DefaultApiLimit.page,
          limit,
        });
      }
    }
  }, [preferenceFilter]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          tableHeight="calc(100vh - 335px)"
          version="v2"
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          rightTopContent={leftTopContent}
          refreshCurrentPages={ReferenceCurrentPages}
          serialNo
          onClearFilterChange={() => {
            if (clearDateRange) {
              clearDateRange();
            }
          }}
          flexDirection
        />
      </div>
    </div>
  );
}

export default AlertsReportTableStation;
